<template>
  <WdsDrawer
    :show="show"
    position="right"
    width="420px"
    padding="0"
    dismissible
    @update:show="emit('update:show', $event)"
  >
    <div v-if="error || (!client && !agencyLink)" class="tw-flex tw-flex-col tw-h-full" tabindex="0">
      <div class="tw-text-right tw-p-3">
        <WdsButtonIcon
          sm
          :aria-label="$t('Close')"
          data-test="client-drawer-close"
          @click="emit('update:show', false)"
        >
          <WdsIcon name="close" :size="16" />
        </WdsButtonIcon>
      </div>
      <div
        v-if="loading && !client && !agencyLink"
        class="tw-mt-[30%] tw-flex tw-justify-center"
        data-test="client-drawer-loading"
      >
        <WdsSpinner :size="40" indeterminate />
      </div>
      <WdsAlert v-else-if="error" error class="tw-mx-4">{{ $t('An error occurred') }}</WdsAlert>
      <WdsText
        v-else-if="result"
        secondary
        class="tw-mx-4"
        :text="$t('This client is not available to you.')"
        data-test="client-drawer-not-found"
      />
    </div>

    <WdsStack v-else direction-column gap-md class="drawer-body tw-h-full" tabindex="0">
      <div class="drawer-chrome">
        <WdsButtonGhost
          secondary
          class="noscroll"
          :href="clientPageUrl"
          data-test="client-drawer-open-page"
          @click="onOpenClientPage"
        >
          <WdsStack gap-sm direction-row align-center justify-center>
            <WdsIcon name="maximize" :size="16" />
            <span>{{ $t('View full page') }}</span>
          </WdsStack>
        </WdsButtonGhost>
        <WdsButtonIcon
          sm
          :aria-label="$t('Close')"
          data-test="client-drawer-close"
          @click="emit('update:show', false)"
        >
          <WdsIcon name="close" :size="16" />
        </WdsButtonIcon>
      </div>

      <div class="tw-px-6" data-test="client-drawer-header">
        <ClientChip
          :client-relation-id="clientRelationId"
          md
          static
          data-test="client-drawer-chip"
        />
      </div>

      <div v-if="agencyLink" class="drawer-content" data-test="agency-client-overview-body">
        <ClientOverviewTab :client="agencyLink" @manage-owners="manageOwnersOpen = true" />
      </div>

      <WdsTabs v-else-if="client" v-model:active-tab-key="activeTab" fitted secondary>
        <WdsTab
          :title="$t('Overview')"
          tab-key="client-overview"
          class="tw-mt-2 tw-mx-6 tw-pb-7"
          data-test="client-overview-tab"
        >
          <ClientOverviewTab
            :client="client"
            @manage-agencies="manageAgenciesOpen = true"
            @manage-owners="manageOwnersOpen = true"
          >
            <!-- The drawer outlives the selection, and the editor binds its save target once. -->
            <ClientCustomFields
              :key="clientRelationId"
              :company-supplier-id="clientRelationId"
              flat
            />
          </ClientOverviewTab>
        </WdsTab>

        <WdsTab
          :title="$t('People')"
          tab-key="client-contacts"
          class="tw-my-2 tw-mx-6"
          data-test="client-contacts-tab"
        >
          <template #title>
            <WdsStack align-center gap-sm>
              {{ $t('People') }}
              <WdsLabel v-if="contactsCount" xs data-test="client-contacts-count">
                {{ contactsCount }}
              </WdsLabel>
            </WdsStack>
          </template>

          <ClientContactsTab
            v-if="seenTabs.has('client-contacts')"
            :company-supplier-id="clientRelationId"
            @open-segment="emit('open-page', clientRelationId, $event)"
          />
        </WdsTab>
      </WdsTabs>
    </WdsStack>
  </WdsDrawer>

  <LinkRecruitersModal
    v-if="client && manageAgenciesOpen"
    :show="manageAgenciesOpen"
    mode="manage"
    :clients="[{ id: client.company.id, name: client.company.name }]"
    @update:show="manageAgenciesOpen = $event"
  />
  <ClientOwnersModal
    v-if="(client || agencyLink) && manageOwnersOpen"
    :show="manageOwnersOpen"
    :client-relation-id="clientRelationId"
    @update:show="manageOwnersOpen = $event"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsAlert,
  WdsButtonGhost,
  WdsButtonIcon,
  WdsDrawer,
  WdsIcon,
  WdsLabel,
  WdsSpinner,
  WdsStack,
  WdsTab,
  WdsTabs,
  WdsText,
} from '@worksome/wds-components'
import { graphql } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import ClientChip from '@/components/Clients/ClientChip.vue'
import ClientOverviewTab from '@/components/Clients/PanelTabs/ClientOverviewTab.vue'
import ClientCustomFields from '@/components/Clients/ClientCustomFields.vue'
import ClientContactsTab from '@/components/Clients/PanelTabs/ClientContactsTab.vue'
import LinkRecruitersModal from '@/components/Clients/LinkRecruitersModal.vue'
import ClientOwnersModal from '@/components/Owners/ClientOwnersModal.vue'
import type { PeopleSegment } from '@/components/Clients/clientPageTabs'
import { ClientDrawerContactsQuery } from '@/components/Clients/graphql/clientDrawerContactsQuery'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

const GET_DRAWER_CLIENT = graphql(`
  query ClientRelationDrawer($id: ID!) {
    clientRelation(id: $id) {
      id
      ... on CompanySupplier {
        company {
          id
          name
        }
      }
      ...ClientChip
      ...ClientOverviewTab
    }
  }
`)

const props = defineProps<{
  show: boolean
  clientRelationId: string
}>()

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void
  (e: 'open-page', companySupplierId: string, segment?: PeopleSegment): void
}>()

// The href stays real so new-tab clicks still work; only the plain click is handed to the host page.
function onOpenClientPage(event: MouseEvent) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return
  }

  event.preventDefault()
  emit('open-page', props.clientRelationId)
}

const activeTab = ref('client-overview')

// WdsTab keeps content in the DOM, so the contacts query only runs once the tab is first opened.
const seenTabs = ref(new Set<string>())

watch(activeTab, (tab) => seenTabs.value.add(tab), { immediate: true })

const manageAgenciesOpen = ref(false)
const manageOwnersOpen = ref(false)

// The drawer stays mounted while closed (so it can animate); only query with a real id.
const { result, loading, error, refetch } = useQuery(
  GET_DRAWER_CLIENT,
  () => ({ id: props.clientRelationId }),
  () => ({ clientId: 'publicApi', enabled: !!props.clientRelationId }),
)

const client = computed(() =>
  result.value?.clientRelation?.__typename === 'CompanySupplier' ? result.value.clientRelation : null,
)

// A staffing agency's link has its own overview.
const agencyLink = computed(() =>
  result.value?.clientRelation?.__typename === 'SupplierClient' ? result.value.clientRelation : null,
)

const { result: contacts } = useQuery(
  ClientDrawerContactsQuery,
  () => ({ client: props.clientRelationId, accounts: [currentAccount.globalId] }),
  () => ({ clientId: 'publicApi', enabled: !!client.value }),
)

const contactsCount = computed(
  () =>
    (contacts.value?.clientTeam?.paginatorInfo.total ?? 0) +
    (contacts.value?.team?.paginatorInfo.total ?? 0) +
    (contacts.value?.recruiters?.paginatorInfo.total ?? 0),
)

const clientPageUrl = computed(() =>
  laravelNamedRoute(namedRoutes.frontend.clients.view, {
    companySupplier: props.clientRelationId,
  }),
)
</script>

<style scoped lang="scss">
// Preflight is off: borders need an explicit style.
.drawer-chrome {
  @apply tw-flex tw-items-center tw-justify-between tw-px-4 tw-pt-4 tw-pb-2;
  @apply tw-border-0 tw-border-b tw-border-solid tw-border-gray-200;
}

// Keeps the close button right-aligned when there is no page link beside it.
.drawer-chrome > :last-child {
  margin-left: auto;
}

.drawer-content {
  @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-6 tw-pt-4 tw-pb-7;
  @apply tw-border-0 tw-border-t tw-border-solid tw-border-gray-200;
}

// Unbounded, the tab content only scrolled once a dropdown overflowed it, clipping the dropdown.
.drawer-body > :deep(.wds-tab-content) {
  @apply tw-min-h-0 tw-flex-1;
}

.drawer-stats {
  @apply tw-mx-6 tw-mt-4 tw-mb-4 tw-py-3;
  @apply tw-border-0 tw-border-t tw-border-b tw-border-solid tw-border-gray-200;
}
</style>
